
/* LE BLU — Interactive continuous gallery v3
   Infinite scroll, manual dragging and Rains-style fullscreen zoom.
*/

(() => {
  const initialized = new WeakSet();
  const mod = (n, m) => ((n % m) + m) % m;

  function setup(gallery) {
    if (initialized.has(gallery)) return;

    const track = gallery.querySelector('[data-leblu-track]');
    const original = track?.querySelector('[data-leblu-set]');

    if (!track || !original) return;

    const originals = [
      ...original.querySelectorAll('.leblu-new-gallery-image')
    ];

    if (!originals.length) return;

    initialized.add(gallery);
    gallery.classList.add('leblu-interactive-gallery');

    const cursor = document.createElement('div');
    cursor.className = 'leblu-gallery-cursor';
    cursor.textContent = '+';
    cursor.setAttribute('aria-hidden', 'true');
    gallery.appendChild(cursor);

    // Disable CSS animation: JavaScript controls the movement.
    track.style.setProperty('animation', 'none', 'important');

    let offset = 0;
    let lastTime = 0;
    let dragging = false;
    let pointerId = null;
    let downX = 0;
    let downY = 0;
    let startOffset = 0;
    let downImage = null;
    let moved = false;
    let suppressClickUntil = 0;

    const SPEED = 38;
    const width = () => original.getBoundingClientRect().width;

    function paint() {
      const w = width();

      if (w > 0) {
        offset = mod(offset, w);
        track.style.transform =
          `translate3d(${-offset}px, 0, 0)`;
      }
    }

    function tick(now) {
      if (lastTime && !dragging && !document.hidden) {
        const dt = Math.min((now - lastTime) / 1000, 0.1);
        offset += SPEED * dt;
        paint();
      }

      lastTime = now;
      requestAnimationFrame(tick);
    }

    requestAnimationFrame(tick);

    function updateCursor(e) {
      const rect = gallery.getBoundingClientRect();

      cursor.style.left = `${e.clientX - rect.left}px`;
      cursor.style.top = `${e.clientY - rect.top}px`;
    }

    function getImageElement(target) {
      return target instanceof Element
        ? target.closest('.leblu-new-gallery-image')
        : null;
    }

    function imageIndex(element) {
      if (!element) return -1;

      const all = [
        ...track.querySelectorAll('.leblu-new-gallery-image')
      ];

      const index = all.indexOf(element);

      return index < 0 ? -1 : index % originals.length;
    }

    gallery.addEventListener('pointermove', e => {
      if (e.pointerType === 'mouse') {
        updateCursor(e);
      }

      if (!dragging || e.pointerId !== pointerId) return;

      const dx = e.clientX - downX;
      const dy = e.clientY - downY;

      if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
        moved = true;
      }

      offset = startOffset - dx;
      paint();
    });

    gallery.addEventListener('pointerdown', e => {
      if (
        e.button !== 0 ||
        e.target.closest(
          '.leblu-new-panel, button, a, input, select, textarea'
        )
      ) {
        return;
      }

      const image = getImageElement(e.target);
      if (!image) return;

      dragging = true;
      moved = false;
      pointerId = e.pointerId;

      downX = e.clientX;
      downY = e.clientY;
      startOffset = offset;
      downImage = image;

      gallery.classList.add('is-dragging');

      if (e.pointerType === 'mouse') {
        e.preventDefault();
        gallery.setPointerCapture(e.pointerId);
      }
    });

    function finish(e, cancelled = false) {
      if (!dragging || e.pointerId !== pointerId) return;

      const wasMoved = moved;
      const clickedImage = downImage;

      dragging = false;
      pointerId = null;
      downImage = null;

      gallery.classList.remove('is-dragging');

      if (gallery.hasPointerCapture(e.pointerId)) {
        gallery.releasePointerCapture(e.pointerId);
      }

      if (wasMoved) {
        suppressClickUntil = performance.now() + 350;
      }

      if (!cancelled && !wasMoved && clickedImage) {
        const index = imageIndex(clickedImage);

        if (index >= 0) open(index);

        suppressClickUntil = performance.now() + 350;
      }
    }

    gallery.addEventListener('pointerup', e => finish(e));

    gallery.addEventListener(
      'pointercancel',
      e => finish(e, true)
    );

    gallery.addEventListener(
      'dragstart',
      e => e.preventDefault()
    );

    gallery.addEventListener('click', e => {
      if (performance.now() < suppressClickUntil) {
        e.preventDefault();
      }
    }, true);

    // Fullscreen zoom

    let overlay = null;
    let active = 0;
    let previousFocus = null;

    const fullImages = originals.map(element => {
      const img = element.querySelector('img');

      const src =
        img?.getAttribute('src') ||
        img?.currentSrc ||
        '';

      const url = new URL(src, location.href);

      if (
        /cdn\.shopify\.com|shopifycdn\.net/.test(
          url.hostname
        )
      ) {
        url.searchParams.set('width', '2400');
      }

      return {
        src: url.href,
        alt: img?.alt || ''
      };
    });

    function show(index) {
      if (!overlay) return;

      active = mod(index, fullImages.length);

      const img = overlay.querySelector(
        '.leblu-zoom-image'
      );

      img.src = fullImages[active].src;
      img.alt = fullImages[active].alt;

      overlay.querySelector(
        '.leblu-zoom-count'
      ).textContent =
        `${active + 1} / ${fullImages.length}`;
    }

    function close() {
      if (!overlay) return;

      overlay.remove();
      overlay = null;

      document.documentElement.classList.remove(
        'leblu-zoom-open'
      );

      document.removeEventListener('keydown', onKey);

      if (
        previousFocus &&
        previousFocus.isConnected &&
        typeof previousFocus.focus === 'function'
      ) {
        previousFocus.focus({ preventScroll: true });
      }
    }

    function onKey(e) {
      if (e.key === 'Escape') {
        close();
      } else if (e.key === 'ArrowLeft') {
        show(active - 1);
      } else if (e.key === 'ArrowRight') {
        show(active + 1);
      } else if (e.key === 'Tab' && overlay) {
        const controls = [
          ...overlay.querySelectorAll('button')
        ];

        const first = controls[0];
        const last = controls[controls.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (
          !e.shiftKey &&
          document.activeElement === last
        ) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    function open(index) {
      if (overlay) return;

      previousFocus = document.activeElement;

      overlay = document.createElement('div');
      overlay.className = 'leblu-zoom-overlay';

      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
      overlay.setAttribute(
        'aria-label',
        'Immagine prodotto ingrandita'
      );

      overlay.innerHTML = `
        <img
          class="leblu-zoom-image"
          alt=""
          draggable="false"
        >

        <button
          class="leblu-zoom-close"
          type="button"
          aria-label="Chiudi zoom"
        >
          <span aria-hidden="true">×</span>
        </button>

        <button
          class="leblu-zoom-prev"
          type="button"
          aria-label="Immagine precedente"
        >‹</button>

        <button
          class="leblu-zoom-next"
          type="button"
          aria-label="Immagine successiva"
        >›</button>

        <span class="leblu-zoom-count"></span>
      `;

      document.body.appendChild(overlay);

      document.documentElement.classList.add(
        'leblu-zoom-open'
      );

      const closeButton = overlay.querySelector(
        '.leblu-zoom-close'
      );

      const previousButton = overlay.querySelector(
        '.leblu-zoom-prev'
      );

      const nextButton = overlay.querySelector(
        '.leblu-zoom-next'
      );

      // The circular X follows the mouse.
      function moveCloseCursor(e) {
        if (e.pointerType !== 'mouse') return;

        closeButton.style.left = `${e.clientX}px`;
        closeButton.style.top = `${e.clientY}px`;
        closeButton.classList.add('is-visible');
      }

      overlay.addEventListener(
        'pointermove',
        moveCloseCursor
      );

      overlay.addEventListener('pointerleave', () => {
        closeButton.classList.remove('is-visible');
      });

      // Clicking the photo or empty background closes zoom.
      overlay.addEventListener('click', e => {
        if (
          e.target.closest(
            '.leblu-zoom-prev, .leblu-zoom-next'
          )
        ) {
          return;
        }

        close();
      });

      previousButton.addEventListener('click', e => {
        e.stopPropagation();
        show(active - 1);
      });

      nextButton.addEventListener('click', e => {
        e.stopPropagation();
        show(active + 1);
      });

      closeButton.addEventListener('click', e => {
        e.stopPropagation();
        close();
      });

      document.addEventListener('keydown', onKey);

      show(index);

      closeButton.focus({ preventScroll: true });
    }
  }

  function init() {
    document.querySelectorAll(
      '[data-leblu-gallery]'
    ).forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      init,
      { once: true }
    );
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
